/**
 * Download View — Companion app download page.
 *
 * Accessible without authentication at /#download so links can be shared.
 * Auto-detects the user's platform and highlights the right download.
 */

import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";

// ── Supabase Storage base URL for companion releases ──
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const STORAGE_BASE = `${SUPABASE_URL}/storage/v1/object/public/companion-releases`;

// ── Artifact definitions ──
type Platform = "macos" | "windows" | "linux";

interface DownloadOption {
  platform: Platform;
  label: string;
  description: string;
  primary: { filename: string; label: string };
  alternative?: { filename: string; label: string };
  icon: string;
}

const a = `viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"`;

const DOWNLOADS: DownloadOption[] = [
  {
    platform: "macos",
    label: "macOS",
    description: "Intel & Apple Silicon",
    primary: {
      filename: "Katanoid-Companion.dmg",
      label: "Download .dmg",
    },
    icon: `<svg ${a}><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z"/><path d="M12 8c-2.2 0-4 1.8-4 4s1.8 4 4 4 4-1.8 4-4-1.8-4-4-4z"/></svg>`,
  },
  {
    platform: "windows",
    label: "Windows",
    description: "Windows 10+",
    primary: {
      filename: "Katanoid-Companion.msi",
      label: "Download .msi",
    },
    alternative: {
      filename: "Katanoid-Companion-setup.exe",
      label: ".exe installer",
    },
    icon: `<svg ${a}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="3" x2="12" y2="21"/></svg>`,
  },
  {
    platform: "linux",
    label: "Linux",
    description: "x86_64",
    primary: {
      filename: "Katanoid-Companion.AppImage",
      label: "Download .AppImage",
    },
    alternative: {
      filename: "Katanoid-Companion.deb",
      label: ".deb package",
    },
    icon: `<svg ${a}><circle cx="12" cy="8" r="4"/><path d="M5 20v-2a7 7 0 0 1 14 0v2"/></svg>`,
  },
];

function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("mac")) return "macos";
  if (ua.includes("win")) return "windows";
  return "linux";
}

function downloadUrl(filename: string): string {
  return `${STORAGE_BASE}/${filename}`;
}

const DOWNLOAD_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;

@customElement("katanoid-download-view")
export class KatanoidDownloadView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @state() private detectedPlatform: Platform = "macos";

  override connectedCallback(): void {
    super.connectedCallback();
    this.detectedPlatform = detectPlatform();
  }

  protected override render(): TemplateResult {
    // Sort cards so detected platform is first
    const sorted = [
      ...DOWNLOADS.filter((d) => d.platform === this.detectedPlatform),
      ...DOWNLOADS.filter((d) => d.platform !== this.detectedPlatform),
    ];

    return html`
      <div class="download-page">
        <div class="download-content">

          <div class="download-hero">
            <div class="download-logo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="32" height="32">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </div>
            <h1 class="download-title">Download Katanoid Companion</h1>
            <p class="download-subtitle">
              Connect your computer to your AI assistant. Control your browser,
              run terminal commands, and access files — all with your approval.
            </p>
          </div>

          <div class="download-cards">
            ${sorted.map(
              (opt) => html`
                <div class="download-card ${opt.platform === this.detectedPlatform ? "download-card--active" : ""}">
                  ${opt.platform === this.detectedPlatform
                    ? html`<span class="download-card-badge">Recommended for you</span>`
                    : nothing}
                  <div class="download-card-header">
                    <span class="download-card-icon" .innerHTML=${opt.icon}></span>
                    <div class="download-card-info">
                      <span class="download-card-name">${opt.label}</span>
                      <span class="download-card-desc">${opt.description}</span>
                    </div>
                  </div>
                  <div class="download-card-actions">
                    <a
                      href="${downloadUrl(opt.primary.filename)}"
                      class="btn btn--primary btn--sm download-btn"
                    >
                      <span class="download-btn-icon" .innerHTML=${DOWNLOAD_ICON}></span>
                      ${opt.primary.label}
                    </a>
                    ${opt.alternative
                      ? html`
                          <a
                            href="${downloadUrl(opt.alternative.filename)}"
                            class="download-alt-link"
                          >
                            or ${opt.alternative.label}
                          </a>
                        `
                      : nothing}
                  </div>
                </div>
              `
            )}
          </div>

          <div class="download-footer">
            <p class="download-version">Latest</p>
            <a href="/#" class="download-back-link">Back to Katanoid</a>
          </div>

          <div class="download-setup-steps">
            <h3>Quick setup</h3>
            <ol>
              <li>Download and install the app for your platform</li>
              <li>Open Katanoid in your browser and go to <strong>Devices</strong></li>
              <li>Click <strong>Pair Device</strong> and enter the code in the companion app</li>
            </ol>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-download-view": KatanoidDownloadView;
  }
}
